import React from "react";
import { MapPin, Clock, HandHeart, Briefcase, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site-data";
import VideoEmbed from "@/components/VideoEmbed";
import Logo from "@/components/Logo";

export default function About() {
  return (
    <section id="sobre" className="py-20 md:py-28 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Sobre nós</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Uma clínica feita de cuidado, <span className="text-orange-600">técnica e acolhimento.</span>
          </h2>
          <p className="mt-5 text-lg text-stone-600 leading-relaxed">
            Há mais de 20 anos, Osvaldo Alves dedica-se a devolver qualidade de vida aos seus pacientes em Cabo Frio.
            Aqui, cada corpo é único — e cada tratamento, também.
          </p>
        </div>

        {/* Bento grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-6 gap-5">
          {/* Large video card */}
          <div className="md:col-span-4 md:row-span-2 rounded-3xl overflow-hidden bg-stone-900 border border-stone-100 relative">
            <div className="relative">
              <VideoEmbed storageKey="about-video" aspect="video" label="Conheça a Clínica Vida Saudável" />
            </div>
            <div className="p-6 md:p-8 text-white bg-gradient-to-b from-stone-900 to-stone-950 flex flex-col sm:flex-row sm:items-center gap-5">
              <Logo className="h-12 w-auto shrink-0" variant="white" />
              <div className="sm:border-l sm:border-white/15 sm:pl-5">
                <h3 className="text-lg md:text-xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Há mais de 20 anos tratando exclusivamente dores na coluna.
                </h3>
                <p className="mt-1.5 text-sm text-white/75">
                  Atendimento individualizado em Cabo Frio — Av. Henrique Terra, 48.
                </p>
              </div>
            </div>
          </div>

          {/* Hours card */}
          <div className="md:col-span-2 rounded-3xl bg-orange-50/70 border border-orange-100 p-7" data-testid="hours-card">
            <div className="flex items-center gap-2.5 text-orange-700">
              <Clock className="w-5 h-5" />
              <span className="text-xs font-bold uppercase tracking-wider">Horários</span>
            </div>
            <div className="mt-4 space-y-3">
              {SITE.hours.map((h) => (
                <div key={h.day} className="flex justify-between gap-3 text-sm">
                  <span className="font-semibold text-stone-800">{h.day}</span>
                  <span className="text-stone-600 text-right">{h.time}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Atendimento com hora marcada
            </div>
          </div>

          {/* Map card */}
          <div className="md:col-span-2 rounded-3xl overflow-hidden bg-white border border-stone-100 flex flex-col" data-testid="map-card">
            <iframe
              title="Localização"
              src={SITE.mapsEmbed}
              className="w-full h-44 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="p-5">
              <div className="flex items-center gap-2 text-orange-700">
                <MapPin className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Endereço</span>
              </div>
              <p className="mt-2 text-sm text-stone-700 leading-relaxed">{SITE.address.full}</p>
              <a href={SITE.mapsLink} target="_blank" rel="noopener noreferrer" data-testid="map-open-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700">
                Abrir no Google Maps <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Community badges (wide) */}
          <div className="md:col-span-6 rounded-3xl bg-gradient-to-br from-orange-600 to-orange-500 text-white p-7 md:p-10 grid md:grid-cols-2 gap-7 items-center" data-testid="community-badges">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-orange-100">Atendimento sem distinção</div>
              <h3 className="mt-2 text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Aqui, todos são bem-vindos. Cuidado com respeito e empatia.
              </h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2.5 bg-white/15 backdrop-blur border border-white/20 px-4 py-2.5 rounded-full text-sm font-semibold">
                <HandHeart className="w-4 h-4" /> Espaço acolhedor para todos
              </div>
              <div className="flex items-center gap-2.5 bg-white/15 backdrop-blur border border-white/20 px-4 py-2.5 rounded-full text-sm font-semibold">
                <Briefcase className="w-4 h-4" /> Apoia Empreendedoras
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
